/**
 * PrePublishScorePanel - Task 5.3 / Phase 16.R7 / W3.F2
 * Scores draft after caption pause (≥400ms). Shows 2–4 actionable
 * improvement suggestions mapped to scoring factors (Class B estimate).
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { TrendingUp, Lightbulb, FiberManualRecord } from '@mui/icons-material';
import { aiContentAPI } from '../services/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import MlEstimateBadge from './MlEstimateBadge';

const DEBOUNCE_MS = 400;

const SIGNAL_FALLBACK_LABELS = {
  caption_too_long: 'Shorten the caption for this platform',
  weak_cta: 'Add a clear call-to-action',
  suboptimal_posting_time: 'Move the schedule into a peak posting window',
  missing_hashtags: 'Add a focused set of relevant hashtags',
  too_many_hashtags: 'Trim hashtags so the set stays focused',
};

function normalizeSuggestions(result) {
  const fromApi = Array.isArray(result?.suggestions)
    ? result.suggestions
        .map((s) => ({
          factor: s?.factor || 'general',
          text: (s?.text || '').trim(),
        }))
        .filter((s) => s.text)
    : [];
  if (fromApi.length > 0) return fromApi.slice(0, 4);
  const signals = result?.signals || [];
  return signals.slice(0, 4).map((sig) => ({
    factor: sig,
    text: SIGNAL_FALLBACK_LABELS[sig] || String(sig).replace(/_/g, ' '),
  }));
}

function PrePublishScorePanel({
  content = '',
  platform,
  clientId,
  scheduledTimeISO,
  contentType = 'image',
}) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debouncedContent = useDebouncedValue(content, DEBOUNCE_MS);

  const platformValue =
    (Array.isArray(platform) ? platform[0] : platform)?.toLowerCase?.() ||
    'facebook';

  const scheduledISO =
    typeof scheduledTimeISO === 'string' && scheduledTimeISO
      ? scheduledTimeISO
      : new Date().toISOString();

  const canScore = Boolean(debouncedContent?.trim?.()?.length > 10 && platformValue);
  const showPlaceholder = !(content?.trim?.()?.length > 10);

  const fetchScore = useCallback(async () => {
    if (!canScore) {
      setResult(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await aiContentAPI.scoreDraft({
        content: debouncedContent || '',
        platform: platformValue,
        content_type: contentType || 'image',
        client_id: clientId || undefined,
        scheduled_time: scheduledISO,
      });
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(err.response?.data?.detail || 'Score unavailable');
    } finally {
      setLoading(false);
    }
  }, [canScore, debouncedContent, platformValue, contentType, clientId, scheduledISO]);

  useEffect(() => {
    if (!canScore) {
      if (showPlaceholder) {
        setResult(null);
        setError(null);
        setLoading(false);
      }
      return undefined;
    }
    fetchScore();
    return undefined;
  }, [canScore, fetchScore, showPlaceholder]);

  if (showPlaceholder && !result) {
    return (
      <Card
        sx={{
          width: '100%',
          bgcolor: 'rgba(22, 27, 34, 0.5)',
          border: '1px solid rgba(48, 54, 61, 0.4)',
          borderRadius: 3,
        }}
      >
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Add content to see predicted score and improvement tips.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading && !result) {
    return (
      <Card
        sx={{
          width: '100%',
          bgcolor: 'rgba(22, 27, 34, 0.5)',
          border: '1px solid rgba(48, 54, 61, 0.4)',
          borderRadius: 3,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (error && !result) {
    return (
      <Card
        sx={{
          width: '100%',
          bgcolor: 'rgba(22, 27, 34, 0.5)',
          border: '1px solid rgba(48, 54, 61, 0.4)',
          borderRadius: 3,
        }}
      >
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Score unavailable. Continue editing—your post will be scored when saved.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const score = result?.predicted_score;
  const suggestions = normalizeSuggestions(result);
  const scoreColor =
    score == null
      ? 'text.secondary'
      : score >= 70
        ? 'success.main'
        : score >= 40
          ? 'warning.main'
          : 'error.main';

  return (
    <Card
      sx={{
        width: '100%',
        bgcolor: 'rgba(22, 27, 34, 0.5)',
        border: '1px solid rgba(48, 54, 61, 0.4)',
        borderRadius: 3,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <TrendingUp sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ color: '#f0f6fc', fontWeight: 600 }}>
            Predicted score
          </Typography>
          <MlEstimateBadge basis="model_prediction" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
          <Typography variant="h4" sx={{ color: scoreColor, fontWeight: 700 }}>
            {score != null ? Math.round(score) : '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            / 100
          </Typography>
        </Box>
        {suggestions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
              <Lightbulb sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                How to improve
              </Typography>
              <MlEstimateBadge basis="model_prediction" />
            </Box>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {suggestions.map((item) => (
                <ListItem
                  key={`${item.factor}-${item.text}`}
                  alignItems="flex-start"
                  disableGutters
                  sx={{ py: 0.35 }}
                >
                  <ListItemIcon sx={{ minWidth: 18, mt: 0.6 }}>
                    <FiberManualRecord sx={{ fontSize: 8, color: 'warning.light' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { color: '#c9d1d9', lineHeight: 1.4 },
                    }}
                    secondary={item.factor !== 'general' ? item.factor.replace(/_/g, ' ') : null}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: { color: '#6e7681', textTransform: 'capitalize' },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {loading && result && (
          <LinearProgress sx={{ mt: 1, borderRadius: 1 }} color="primary" />
        )}
      </CardContent>
    </Card>
  );
}

export default memo(PrePublishScorePanel);
